/** Piezas de interfaz pequeñas y reutilizables. */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { formatMinutes, type RecordingStatus, type UsageSummary } from '@eco/shared'
import { ESTADO_KIND, ESTADO_LABEL } from '../lib/format'
import { IconClose } from './icons'
import { Logo } from './Logo'

export function Brand({ to = '/', height = 24 }: { to?: string; height?: number }) {
  return (
    <Link to={to} className="brand" aria-label="eco, inicio">
      <Logo height={height} title="eco" />
    </Link>
  )
}

/**
 * Menú desplegable accesible: botón con aria-expanded, se cierra con Escape, al
 * hacer clic fuera o al elegir. Los ítems son <button role="menuitem">.
 */
export function Menu({
  label,
  button,
  children,
  align = 'right',
  className = 'btn'
}: {
  label: string
  button: ReactNode
  children: (close: () => void) => ReactNode
  align?: 'left' | 'right'
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    // Foco al primer ítem para usarlo con teclado
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div className="menu-wrap" ref={ref}>
      <button
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        {button}
      </button>
      {open && (
        <div className={`menu ${align === 'left' ? 'left' : ''}`} role="menu" aria-label={label}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}

export function StatusBadge({ estado }: { estado: RecordingStatus }) {
  return (
    <span className={`pill ${ESTADO_KIND[estado]}`}>
      <span className="dot" aria-hidden="true" />
      {ESTADO_LABEL[estado]}
    </span>
  )
}

export function UsageMeter({ usage, compact = false }: { usage: UsageSummary; compact?: boolean }) {
  const pct = usage.minutosIncluidos
    ? Math.min(100, (usage.minutosUsados / usage.minutosIncluidos) * 100)
    : 100
  const reinicio = new Date(usage.periodoFin).toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short'
  })
  const cls = pct >= 100 ? 'over' : pct >= 80 ? 'warn' : ''
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className={compact ? 'faint' : 'muted'} style={{ fontSize: compact ? 12 : 13 }}>
        <strong style={{ color: 'var(--text)' }}>{formatMinutes(usage.minutosRestantes)}</strong>{' '}
        disponibles de {formatMinutes(usage.minutosIncluidos)}
        {compact ? '' : ` · se reinicia el ${reinicio}`}
      </div>
      <div
        className={`bar ${cls}`}
        role="progressbar"
        aria-label="Horas usadas este mes"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <span style={{ width: `${pct}%` }} />
      </div>
      {usage.grabacionesRetenidas > 0 && (
        <Link to="/plan" className="faint" style={{ fontSize: 12 }}>
          {usage.grabacionesRetenidas} grabación{usage.grabacionesRetenidas === 1 ? '' : 'es'}{' '}
          esperando horas →
        </Link>
      )}
    </div>
  )
}

/** Diálogo modal nativo (<dialog>): foco atrapado y Escape gratis. */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  useLayoutEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) {
      d.showModal?.()
      // `autoFocus` no sirve dentro del diálogo (React enfoca antes de abrirlo): usar data-autofocus
      d.querySelector<HTMLElement>('[data-autofocus]')?.focus()
    }
    if (!open && d.open) d.close?.()
  }, [open])
  return (
    <dialog ref={ref} onClose={onClose} aria-labelledby="dlg-title">
      {open && (
        <>
          <div className="dlg-head">
            <h2 id="dlg-title" className="grow">
              {title}
            </h2>
            <button className="iconbtn" onClick={onClose} aria-label="Cerrar">
              <IconClose />
            </button>
          </div>
          <div className="dlg-body">{children}</div>
          {footer && <div className="dlg-foot">{footer}</div>}
        </>
      )}
    </dialog>
  )
}

export function Skeleton({ h = 16, w = '100%' }: { h?: number; w?: number | string }) {
  return <div className="skeleton" style={{ height: h, width: w }} aria-hidden="true" />
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Algo salió mal.'
  return (
    <div className="notice error row">
      <span className="grow">{msg}</span>
      {onRetry && (
        <button className="btn sm" onClick={onRetry}>
          Reintentar
        </button>
      )}
    </div>
  )
}
